import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Check, Clock, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { Order } from '../context/AppContext';
import { quotesApi } from '../api/quotes';
import { FieldError } from '../components/FieldError';
import { AuthErrorBanner } from '../components/AuthErrorBanner';

export default function QuoteRequestPage() {
  usePageTitle("Demande de devis — Lodiss");

  const { lang, cart, clearCart, addOrder, setPage, currentUser } = useApp();
  const T = t[lang];

  const [form, setForm] = useState({ 
    name: currentUser?.full_name || '', 
    company: currentUser?.company || '', 
    phone: currentUser?.phone || '', 
    email: currentUser?.email || '', 
    notes: '' 
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [sent, setSent] = useState(false);
  const [ref, setRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    
    if (!form.name.trim()) {
      e.name = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    if (!form.company.trim()) {
      e.company = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    if (!form.email.trim() || !form.email.includes('@')) {
      e.email = lang === 'fr' ? "L'adresse email n'est pas valide" : 'The email address is not valid';
    }
    if (!form.phone.trim()) {
      e.phone = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    } else if (!/^\d+$/.test(form.phone.replace(/\s/g, ''))) {
      e.phone = lang === 'fr' ? 'Numéro de téléphone invalide' : 'Invalid phone number';
    } else if (form.phone.replace(/\s/g, '').length < 8) {
      e.phone = lang === 'fr' ? 'Le numéro doit contenir au moins 8 chiffres' : 'Phone number must be at least 8 digits';
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInputChange = (key: keyof typeof form, value: string) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors(e => ({ ...e, [key]: '' }));
    }
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validate()) return;
    
    const id = 'QUO-' + Date.now().toString(36).toUpperCase();
    setRef(id);
    
    const quoteItems = cart.map(item => ({
      product_id: item.product.id,
      quantity: item.qty,
    }));
    
    const apiQuote = {
      items: quoteItems,
      form_data: form,
    };
    
    setSubmitting(true);
    
    try {
      await quotesApi.createQuote(apiQuote);
      
      const order: Order = {
        id,
        type: 'quote',
        items: [...cart],
        date: new Date().toISOString(),
        status: lang === 'fr' ? 'En attente' : 'Pending',
        form,
      };
      addOrder(order);
      clearCart();
      setSent(true);
    } catch (err: any) {
      console.error('Failed to create quote:', err);
      
      if (err.fieldErrors && typeof err.fieldErrors === 'object') {
        const fieldErrors: Record<string, string> = {};
        Object.entries(err.fieldErrors).forEach(([key, value]) => {
          fieldErrors[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setErrors(fieldErrors);
      } else if (err.status === 422) {
        setErrors(err.details || {});
      } else if (err.status === 500 || err.message?.toLowerCase().includes('server')) {
        setSubmitError(
          lang === 'fr' 
            ? "L'envoi du devis a échoué. Veuillez réessayer ou nous contacter directement." 
            : 'Quote submission failed. Please try again or contact us directly.'
        );
      } else {
        setSubmitError(
          lang === 'fr' 
            ? "L'envoi du devis a échoué. Veuillez réessayer ou nous contacter directement." 
            : 'Quote submission failed. Please try again or contact us directly.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key: keyof typeof form, label: string, type = 'text', required = false) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={e => handleInputChange(key, e.target.value)}
        disabled={submitting}
        className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
          errors[key]
            ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
            : 'border-gray-200 focus:border-amber-400 focus:ring-amber-100'
        } ${submitting ? 'bg-gray-50 cursor-not-allowed' : ''}`}
      />
      <FieldError message={errors[key]} />
    </div>
  );

  if (sent) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-950 mb-2">{T.quoteRequest.success}</h2>
            <p className="text-sm text-gray-500 mb-3">{T.quoteRequest.successMsg}</p>
            <div className="flex items-center justify-center gap-2 bg-amber-50 rounded-lg p-3 mb-6">
              <Clock size={14} className="text-amber-600" />
              <p className="text-xs font-medium text-amber-700">
                {lang === 'fr' ? 'Réponse sous 24h' : 'Response within 24h'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-400">{lang === 'fr' ? 'Référence devis' : 'Quote reference'}</p>
              <p className="text-sm font-bold text-gray-900 font-mono mt-0.5">{ref}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setPage('orders')}
                className="w-full py-2.5 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
              >
                {T.common.myOrders}
              </button>
              <button
                onClick={() => setPage('home')}
                className="w-full py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-950">{T.quoteRequest.title}</h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
            <Clock size={13} className="text-amber-500" />
            {lang === 'fr' ? 'Réponse garantie sous 24h avec tarifs dégressifs.' : 'Guaranteed response within 24h with tiered pricing.'}
          </p>
        </div>

        {cart.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {lang === 'fr' ? 'Produits concernés' : 'Products included'}
            </h3>
            <ul className="space-y-2">
              {cart.map(item => (
                <li key={item.product.id} className="flex items-center gap-3 text-sm">
                  <img src={item.product.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
                  <span className="flex-1 text-gray-700">
                    {lang === 'fr' ? item.product.name : item.product.nameEn}
                  </span>
                  <span className="text-gray-500 font-medium">x{item.qty}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    item.qty >= item.product.quoteThreshold
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {item.qty >= item.product.quoteThreshold ? (lang === 'fr' ? 'Devis' : 'Quote') : (lang === 'fr' ? 'Direct' : 'Direct')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
          {submitError && (
            <AuthErrorBanner error={submitError} onClear={() => setSubmitError('')} />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('name', T.quoteRequest.name, 'text', true)}
            {field('company', T.quoteRequest.company, 'text', true)}
            {field('phone', T.quoteRequest.phone, 'tel', true)}
            {field('email', T.quoteRequest.email, 'email', true)}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">{T.quoteRequest.notes}</label>
            <textarea
              value={form.notes}
              onChange={e => handleInputChange('notes', e.target.value)}
              disabled={submitting}
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100 transition-colors resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">{T.quoteRequest.upload}</label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg px-4 py-3 text-center hover:border-gray-300 transition-colors cursor-pointer">
              <p className="text-xs text-gray-400">
                {lang === 'fr' ? 'Glissez un fichier ou cliquez pour parcourir (mock)' : 'Drag a file or click to browse (mock)'}
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {lang === 'fr' ? 'Envoi en cours...' : 'Submitting...'}
              </>
            ) : (
              T.quoteRequest.submit
            )}
          </button>
        </form>
      </div>
    </main>
  );
}
